import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AuthConfig, AuthConfigService, AuthFacade, AuthRoutes } from '@rx-applications/shared/auth/data-access';
import { FormUtils } from '@rx-applications/shared/utils-forms';
import { addAbsolutePathPrefix } from '@rx-applications/shared/utils';
import { CommonModule } from '@angular/common';
import { TRANSLATION_SCOPE, translationProvider } from './i18n/translation.provider';
import { TranslocoModule } from '@ngneat/transloco';
import { RxAlertModule, RxButtonModule, RxInputModule } from '@roomex/rx-components';
import { RouterLink } from '@angular/router';
import { PasswordMatchFormComponent, PasswordPolicyComponent } from '@rx-applications/shared/auth/ui';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'rx-signup',
  templateUrl: './signup.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    RxButtonModule,
    RouterLink,
    RxAlertModule,
    ReactiveFormsModule,
    RxInputModule,
    PasswordMatchFormComponent,
    PasswordPolicyComponent,
  ],
  providers: [translationProvider],
})
export class SignupComponent extends FormUtils implements OnInit, OnDestroy {
  protected readonly TRANSLATION_SCOPE = TRANSLATION_SCOPE;
  protected readonly addAbsolutePathPrefix = addAbsolutePathPrefix;
  protected readonly authConfig: AuthConfig = inject(AuthConfigService).config;
  authRoutes = AuthRoutes;

  form: UntypedFormGroup;

  constructor(public authFacade: AuthFacade, private fb: UntypedFormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/[a-zA-Z]+\s+[a-zA-Z]+/)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/[\d\s-+()]{6,}/)]],
      emailAddress: [''],
      websiteId: [''],
    });
  }

  ngOnDestroy() {
    // When a user sign up and then, go to another route and come back
    // to the forgot password page, it shows the success notification. The
    // below action reset the state when component is destroyed.
    this.authFacade.hideSuccessMessage();
    this.authFacade.hideErrorMessage();
  }

  onSubmit(authConfig: AuthConfig) {
    this.form.controls['emailAddress'].setValidators([
      Validators.required,
      Validators.email,
      this.validateDomain(authConfig.selfSignup.restrictedDomains),
    ]);
    this.form.controls['emailAddress'].updateValueAndValidity();

    if (this.form.invalid) {
      this.markAllAsTouched(this.form);
      return;
    }

    this.authFacade.register({
      ...this.form.value,
      ...{ websiteId: authConfig.websiteId },
    });
  }

  private validateDomain(domains: string[] = []): ValidatorFn {
    return (control: AbstractControl) => {
      if (domains.length > 0 && control.value && !(control.hasError('email') || control.hasError('required'))) {
        if (!domains.some((value) => control.value.endsWith(value))) {
          return { validDomain: true };
        }
      }
      return null;
    };
  }
}
